import { useContext, useState } from 'react';
import styled from 'styled-components';
import LightBulb from 'design-system/src/assets/icons/lightBulb.svg?react';
import HelpChatOutline from 'design-system/src/assets/icons/helpChatOutline.svg?react';
import NewspaperOutline from 'design-system/src/assets/icons/newspaperOutline.svg?react';
import CalendarOutline from 'design-system/src/assets/icons/calendarOutline.svg?react';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import PageContainer from '../../components/PageContainer';
import { AuthContext } from '../../auth';
import { StyledContainer } from '../../components/tableItemDetailViewComponents/Containers';
import { PROVIDER_SUPPORT_CONTACT_INFO } from '../../constants';
import ScheduleTrainingModal from '../../components/ScheduleTrainingModal/ScheduleTrainingModal';
import Breadcrumb from 'design-system/src/components/Breadcrumb/Breadcrumb';
import { useLocation, Outlet } from 'react-router-dom';

const HelpPageContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 48px;
`;

const HelpCenterCardsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
`;

const StyledHelpCenterCard = styled(StyledContainer)`
  box-sizing: border-box;
  min-width: 232px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  flex: 1;
`;

const FlexGrow = styled.div`
  flex-grow: 1;
`;

const InlineLink = styled(Button)`
  display: inline-block;
`;

export type HelpCenterCardProps = {
  icon: React.ReactNode;
  title: string;
  description?: string;
  footerContent: React.ReactNode;
};

/**
 * A card on the help center page that links to some resource.
 */
export const HelpCenterCard = ({ icon, title, description, footerContent }: HelpCenterCardProps) => {
  return (
    <StyledHelpCenterCard>
      {icon}

      <FlexGrow>
        <Typography renderedAs="h2" styledAs="bodyLargeSpaceGrotesk" weight="500">
          {title}
        </Typography>
        {description && <Typography styledAs="bodyExtraSmallDMSans">{description}</Typography>}
      </FlexGrow>

      {footerContent}
    </StyledHelpCenterCard>
  );
};

/**
 * Help center page with links to various resources.
 */
export const HelpCenter = () => {
  const { user } = useContext(AuthContext);
  const [isScheduleTrainingModalOpen, setIsScheduleTrainingModalOpen] = useState(false);
  const location = useLocation();

  const cardsSecondRow = [
    {
      icon: <NewspaperOutline width={24} height={24} aria-hidden />,
      title: 'User Manual',
      description: 'Learn more about how Tandem works.',
      footerContent: (
        <Button
          size="sm"
          variant="tertiary"
          href="https://withtandem.com/manual"
          target="_blank"
          rel="noopener noreferrer"
        >
          View manual <span className="sr-only">opens in a new tab</span>
        </Button>
      ),
    },
    {
      icon: <CalendarOutline width={24} height={24} aria-hidden />,
      title: 'Schedule Training',
      description: 'Book a training session with a member of our team.',
      footerContent: (
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => {
            setIsScheduleTrainingModalOpen(true);
          }}
        >
          Schedule a session <span className="sr-only">opens in a new tab</span>
        </Button>
      ),
    },
  ];
  if (user?.practice_training_video_link) {
    // insert card at the 1st position
    cardsSecondRow.unshift({
      icon: <LightBulb width={24} height={24} aria-hidden />,
      title: 'Tutorial',
      description: 'Check out our interactive tutorial for how to use Tandem.',
      footerContent: (
        <Button size="sm" variant="tertiary" to="/help-center/tutorial">
          Start Tutorial
        </Button>
      ),
    });
  }

  const emailLink = (
    <InlineLink
      variant="link"
      textStyledAs="bodyExtraSmallDMSans"
      href={`mailto:${PROVIDER_SUPPORT_CONTACT_INFO.email}`}
    >
      {PROVIDER_SUPPORT_CONTACT_INFO.email}
    </InlineLink>
  );

  let body;
  const crumbs = [{ title: 'Help home', link: '/help-center' }];
  // TODO: create a helper function to create breadcrumbs based on the current path
  if (location.pathname.includes('/help-center/new-task')) {
    body = <Breadcrumb currentCrumb="Create new task" prevCrumbs={crumbs} />;
  } else if (location.pathname.includes('/help-center/tutorial')) {
    body = <Breadcrumb currentCrumb="Tutorial" prevCrumbs={crumbs} />;
  } else {
    body = (
      <>
        {/* if the user doesn't have the tasks tab, don't show the top card because it just has a button to create a task */}
        {user?.tabs.includes('tasks') && (
          <HelpCenterCardsRow>
            <HelpCenterCard
              icon={<HelpChatOutline aria-hidden />}
              title="Questions about a specific patient or prescription? "
              footerContent={
                <Button size="sm" to="/help-center/new-task?source=portal_help_center">
                  Create a task to get help
                </Button>
              }
            />
          </HelpCenterCardsRow>
        )}
        <HelpCenterCardsRow>
          {cardsSecondRow.map((card) => (
            <HelpCenterCard key={card.title} {...card} />
          ))}
        </HelpCenterCardsRow>
        <Hairline />

        <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--dark-gray)">
          For technical issues and questions about using Tandem, you can contact our support team for help.
        </Typography>
        <Typography styledAs="bodyExtraSmallSpaceGrotesk" color="var(--dark-gray)">
          Email: {emailLink}
        </Typography>
      </>
    );
  }

  return (
    <PageContainer title="Help Center" width="fitContent">
      <HelpPageContents>
        {body}
        <Outlet />
      </HelpPageContents>
      {isScheduleTrainingModalOpen && <ScheduleTrainingModal onClose={() => setIsScheduleTrainingModalOpen(false)} />}
    </PageContainer>
  );
};

export default HelpCenter;
